import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtTime, apiError } from "@/lib/api";
import { AppuntamentoModal } from "@/components/AppuntamentoModal";
import { MobileAgenda } from "@/components/MobileAgenda";
import { RichiestePanel } from "@/components/RichiestePanel";
import { AgendaTimeGrid } from "@/components/AgendaTimeGrid";
import { tipoColor, TIPO_META, TIPO_ORDER } from "@/lib/agendaTipi";
import { AgendaSidebar, QuickCreate, EventPopover, MonthGrid, ListView, agendaKinds, personColor } from "@/components/AgendaGCal";
import { Modal, Field, Row } from "@/pages/Notizie";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Users, Loader2, Menu, Trash2 } from "lucide-react";
import {
  format, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
} from "date-fns";
import { it } from "date-fns/locale";
import { conRuolo } from "@/lib/ruoli";
import { BarraElenco, Casella, SelezionaTutto, azioniCestino, useSelezione } from "@/components/SelezioneFile";

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

import { aggiornaConAnnulla } from "@/lib/agendaMove";

export default function Agenda() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const canMulti = ["direzione", "segreteria", "direttore_commerciale"].includes(user?.role) || user?.is_segreteria;
  const canAvailability = canMulti;
  const isSocio = user?.role === "direzione" || (user?.roles || []).includes("direzione") || !!user?.is_direzione;
  const KINDS = agendaKinds(isSocio);
  const VIEWS = [["giorno", "Giorno"], ["settimana", "Settimana"], ["mese", "Mese"], ["elenco", "Elenco"], ...(canAvailability ? [["disponibilita", "Disponibilità"]] : []), ["richieste", "Richieste"]];
  const LSKEY = `agenda_filters_v2_${user?.id}`;

  const [events, setEvents] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("settimana");
  useEffect(() => { if (searchParams.get("view") === "richieste") setView("richieste"); }, [searchParams]);
  const [modal, setModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [prefill, setPrefill] = useState({});
  const [gridKey, setGridKey] = useState(0);
  const saved = (() => { try { return JSON.parse(localStorage.getItem(LSKEY)) || {}; } catch { return {}; } })();
  const [availMode, setAvailMode] = useState(saved.availMode || "giorno");
  const [bulkOpen, setBulkOpen] = useState(false);
  const canReassign = ["direzione", "segreteria"].includes(user?.role) || user?.is_segreteria || user?.is_direzione;
  const [hidden, setHidden] = useState(saved.hidden || []);
  const [team, setTeam] = useState(saved.team || "");
  const [selKinds, setSelKinds] = useState(() => {
    const k = saved.kinds || KINDS.map((x) => x[0]);
    return saved.kindsUff || k.includes("riunione_ufficio") || !KINDS.some((x) => x[0] === "riunione_ufficio") ? k : [...k, "riunione_ufficio"];
  });
  const [sideOpen, setSideOpen] = useState(saved.side !== false);
  const [colorBy, setColorBy] = useState(saved.colorBy || "tipo");
  const [quick, setQuick] = useState(null);
  const [pop, setPop] = useState(null);
  const [cestinoOpen, setCestinoOpen] = useState(false);
  const lastPt = useRef(null);
  useEffect(() => {
    const h = (e) => { lastPt.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("pointerdown", h, true);
    return () => window.removeEventListener("pointerdown", h, true);
  }, []);
  const [bloccoOpen, setBloccoOpen] = useState(false);
  const [occBlock, setOccBlock] = useState(null);
  const [daClass, setDaClass] = useState([]);
  const [daClassOpen, setDaClassOpen] = useState(false);
  useEffect(() => { api.get("/agenda/da-classificare").then((r) => setDaClass(r.data || [])).catch(() => {}); }, [events]);

  useEffect(() => { localStorage.setItem(LSKEY, JSON.stringify({ hidden, team, kinds: selKinds, kindsUff: true, availMode, side: sideOpen, colorBy })); }, [hidden, team, selKinds, availMode, sideOpen, colorBy]); // eslint-disable-line
  useEffect(() => { api.get("/agenda/circle-members", { params: { tutti: true } }).then((r) => setAgentsList(r.data || [])).catch(() => {}); }, []);
  const [ambito, setAmbito] = useState(null);
  useEffect(() => { api.get("/agenda/vista").then((r) => setAmbito(r.data.vista)).catch(() => setAmbito("tutti")); }, []);
  const scegliAmbito = async (v) => {
    try { await api.put("/agenda/vista", { vista: v }); setAmbito(v); setGridKey((k) => k + 1); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/agenda/events"), api.get("/chiamate").catch(() => ({ data: [] }))]);
    const calls = (c.data || []).filter((x) => x.chiamata_entro).map((x) => ({
      id: `call-${x.tipo}-${x.id}`, kind: "chiamata", data: `${x.chiamata_entro}T09:00:00`, titolo: `Chiamare: ${x.nome}`, tipo: "chiamata",
    }));
    setEvents([...(a.data || []), ...calls]);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const teams = useMemo(() => [...new Set(agentsList.map((a) => a.team_name).filter(Boolean))], [agentsList]);
  const visibleAgents = team ? agentsList.filter((a) => a.team_name === team) : agentsList;

  const knownIds = useMemo(() => new Set(agentsList.map((a) => a.id)), [agentsList]);
  const teamIds = useMemo(() => (team ? new Set(visibleAgents.map((a) => a.id)) : null), [team, agentsList]); // eslint-disable-line
  const personOk = (e) => {
    if (agentsList.length < 2) return true;
    const ids = [e.owner_agent_id, ...(e.partecipanti_ids || [])].filter((i) => i && knownIds.has(i));
    return !ids.length || ids.some((i) => !hidden.includes(i) && (!teamIds || teamIds.has(i)));
  };
  const filtered = useMemo(() => events.filter((e) => {
    if (!personOk(e)) return false;
    if (e.kind === "proposta_scadenza") return selKinds.includes("scadenza");
    if (e.kind === "visita") return selKinds.includes("acquirente");
    if (e.kind === "chiamata") return true;
    if (e.da_classificare) return true;
    if (e.tipo && TIPO_META[e.tipo]) return selKinds.includes(e.tipo);
    return true;
  }), [events, selKinds, hidden, teamIds, knownIds]); // eslint-disable-line

  const evByDay = useMemo(() => {
    const map = {};
    filtered.forEach((e) => { if (!e.data) return; const key = e.data.slice(0, 10); (map[key] = map[key] || []).push(e); });
    Object.values(map).forEach((l) => l.sort((a, b) => (a.data || "").localeCompare(b.data || "")));
    return map;
  }, [filtered]);

  const weekStep = view === "settimana" || (view === "disponibilita" && availMode === "settimana");
  const prev = () => setCursor(view === "mese" ? subMonths(cursor, 1) : (weekStep || view === "elenco") ? subWeeks(cursor, 1) : subDays(cursor, 1));
  const next = () => setCursor(view === "mese" ? addMonths(cursor, 1) : (weekStep || view === "elenco") ? addWeeks(cursor, 1) : addDays(cursor, 1));
  const periodo = view === "mese" ? ["Mese successivo", "Mese precedente"] : (weekStep || view === "elenco") ? ["Settimana successiva", "Settimana precedente"] : ["Giorno successivo", "Giorno precedente"];
  const navOk = view !== "richieste";
  const overlayOpen = !!(modal || quick || pop || bloccoOpen || occBlock || bulkOpen || daClassOpen);
  // Scorciatoie come Google Calendar (solo se non si sta scrivendo e nessuna finestra è aperta).
  useEffect(() => {
    const h = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || overlayOpen || window.innerWidth < 768) return;
      const t = e.target;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      if (document.querySelector("[role=dialog], [data-testid=unsaved-dialog]")) return;
      const k = e.key.toLowerCase();
      if ((k === "arrowright" || k === "j") && navOk) { e.preventDefault(); next(); }
      else if ((k === "arrowleft" || k === "k") && navOk) { e.preventDefault(); prev(); }
      else if (k === "t") setCursor(new Date());
      else if (k === "g") setView("giorno");
      else if (k === "s") setView("settimana");
      else if (k === "m") setView("mese");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  const wheel = useRef({ acc: 0, until: 0 });
  const onGridWheel = (e) => {
    if (!navOk || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    const w = wheel.current, now = Date.now();
    if (now < w.until) return;
    w.acc += e.deltaX;
    if (Math.abs(w.acc) > 80) { if (w.acc > 0) next(); else prev(); w.acc = 0; w.until = now + 700; }
  };
  const swipe = useRef(null);
  const onGridTouchStart = (e) => { swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onGridTouchEnd = (e) => {
    const s = swipe.current; swipe.current = null;
    if (!s || !navOk) return;
    const dx = e.changedTouches[0].clientX - s.x, dy = e.changedTouches[0].clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) { if (dx < 0) next(); else prev(); }
  };
  const ws = startOfWeek(cursor, { weekStartsOn: 1 }), we = endOfWeek(cursor, { weekStartsOn: 1 });
  const label = view === "settimana" && ws.getMonth() !== we.getMonth()
    ? `${cap(format(ws, "MMM", { locale: it }))} – ${cap(format(we, "MMM yyyy", { locale: it }))}`
    : cap(format(cursor, "MMMM yyyy", { locale: it }));
  const subLabel = (view === "giorno" || (view === "disponibilita" && availMode === "giorno")) ? format(cursor, "EEEE d", { locale: it }) : "";

  const quickAt = (day, h = null) => { const d = new Date(day); const n = new Date(); d.setHours(h ?? Math.min(Math.max(n.getHours() + 1, 8), 20), 0, 0, 0); setQuick({ startISO: format(d, "yyyy-MM-dd'T'HH:mm:ss"), durMin: 60, pt: h === null ? { x: 300, y: 160 } : lastPt.current }); };
  const more = (pf) => { setQuick(null); setEditEvent(null); setPrefill(pf); setModal(true); };
  const pickDay = (d) => { setCursor(d); if (!["settimana", "disponibilita"].includes(view)) setView("giorno"); };
  const onEventClick = (e) => {
    if (e.masked) {
      const chi = e.owner_name || nameById[e.owner_agent_id] || "un collega";
      const s = new Date(e.data), f = new Date(s.getTime() + (e.durata_min || 60) * 60000);
      toast(`${e.block_id ? "Occupato ·" : "Appuntamento di"} ${chi} – dalle ${fmtTime(s.toISOString())} alle ${fmtTime(f.toISOString())}`);
      return;
    }
    if (e.tipo === "bloccato") {
      setOccBlock(e);
      return;
    }
    if (e.link && e.kind !== "appuntamento") { nav(e.link); return; }
    if (e.google_busy || String(e.id).startsWith("call-") || e.kind !== "appuntamento") return;
    setPop({ e, pt: lastPt.current });
  };
  const openSlot = (agentId, iso) => { setEditEvent(null); setPrefill({ agentId, date: iso.slice(0, 16) }); setModal(true); };
  const onBlockClick = (b, nome) => {
    const ev = b.appuntamento_id && events.find((x) => x.id === b.appuntamento_id);
    if (ev) onEventClick(ev);
    else toast(b.source === "blocco" ? `Occupato · ${nome}` : `Appuntamento di ${nome}`);
  };
  const togglePerson = (id) => setHidden((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleKind = (k) => setSelKinds((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const weekDays = useMemo(() => eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) }), [cursor]);
  const nameById = useMemo(() => {
    const m = {};
    (agentsList || []).forEach((a) => { if (a.id) m[a.id] = a.name; });
    if (user?.id) m[user.id] = user.name;
    return m;
  }, [agentsList, user]);
  const colorForEvent = (e) => (e.google_busy || (e.block_id && !e.masked) ? "#9CA3AF" : e.kind === "occupato" ? (knownIds.has(e.owner_agent_id) ? personColor(agentsList, e.owner_agent_id) : "#9CA3AF") : (colorBy === "persona" && agentsList.length > 1 && knownIds.has(e.owner_agent_id)) ? personColor(agentsList, e.owner_agent_id) : tipoColor(e));
  const people = agentsList.length > 1 ? visibleAgents.map((a) => ({ ...a, color: personColor(agentsList, a.id) })) : null;
  const canEditEvent = (e) => e.kind === "appuntamento" && e.tipo !== "bloccato" && !e.masked && !e.google_busy && !String(e.id).startsWith("call-") && e.can_edit !== false
    && (canMulti || e.owner_agent_id === user?.id || (e.partecipanti_ids || []).includes(user?.id));
  const createAt = ({ startISO, durMin }) => setQuick({ startISO, durMin, pt: lastPt.current });
  const reload = () => { setGridKey((k) => k + 1); load(); };
  const moveEvent = (e, newISO) => aggiornaConAnnulla(e, { data: newISO }, reload);
  const resizeEvent = (e, newDur) => { if (newDur !== (e.durata_min || 60)) aggiornaConAnnulla(e, { durata_min: newDur }, reload); };
  const scrollKey = `${view}-${format(cursor, "yyyy-MM-dd")}`;

  const sidebar = (
    <AgendaSidebar cursor={cursor} onPick={pickDay} onCrea={() => quickAt(cursor)} onBlocca={() => setBloccoOpen(true)}
      kinds={KINDS} selKinds={selKinds} toggleKind={toggleKind} people={people} hidden={hidden} togglePerson={togglePerson}
      teams={teams} team={team} setTeam={setTeam} colorBy={colorBy} setColorBy={setColorBy} />
  );
  return (
    <>
    <div className="fade-up hidden md:block" data-testid="agenda-page">
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="agenda-topbar">
        <button data-testid="agenda-toggle-sidebar" onClick={() => setSideOpen((v) => !v)} title="Mostra/nascondi colonna" className="p-2.5 rounded-full hover:bg-slate-100 transition-colors"><Menu size={20} /></button>
        <button onClick={() => setCursor(new Date())} data-testid="cal-today" title="Oggi (T)" className="px-5 py-2 rounded-full border border-[var(--border)] text-sm font-bold text-[#2B2B2B] hover:bg-slate-50 transition-colors">Oggi</button>
        <div className="flex">
          <button onClick={prev} data-testid="cal-prev" title={`${periodo[1]} (← o K)`} aria-label={periodo[1]} className="p-2.5 rounded-full hover:bg-slate-100 active:scale-95 transition-transform"><ChevronLeft size={28} /></button>
          <button onClick={next} data-testid="cal-next" title={`${periodo[0]} (→ o J)`} aria-label={periodo[0]} className="p-2.5 rounded-full hover:bg-slate-100 active:scale-95 transition-transform"><ChevronRight size={28} /></button>
        </div>
        <h1 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight text-[#2B2B2B]" data-testid="cal-label">{label}{subLabel && <span className="ml-3 text-base font-semibold text-slate-400 capitalize">{subLabel}</span>}</h1>
        {(user?.role === "direzione" || user?.is_segreteria || user?.role === "segreteria") && <button data-testid="agenda-cestino-btn" onClick={() => setCestinoOpen(true)} title="Cestino agenda" className="p-2.5 rounded-full hover:bg-slate-100"><Trash2 size={18} /></button>}
        <div className="ml-auto flex bg-slate-100 rounded-full p-1" data-testid="agenda-view-switch">
          {VIEWS.map(([k, l]) => (
            <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === k ? "bg-[#2B2B2B] text-white shadow" : "text-slate-600 hover:text-[#2B2B2B]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <AmbitoSwitch ambito={ambito} onPick={scegliAmbito} colleghi={agentsList.filter((a) => a.id !== user?.id)} />

      {daClass.length > 0 && (
        <button data-testid="da-classificare-banner" onClick={() => setDaClassOpen(true)}
                className="w-full mb-4 flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors">
          <span>{daClass.length} appuntament{daClass.length === 1 ? "o" : "i"} da classificare</span>
          <span className="text-xs underline">Sistema ora →</span>
        </button>
      )}

      <div className="flex gap-6 items-start">
      {sideOpen && sidebar}
      <main className="flex-1 min-w-0" data-testid="agenda-grid-area" onWheel={onGridWheel} onTouchStart={onGridTouchStart} onTouchEnd={onGridTouchEnd}>
      {view === "mese" && <MonthGrid cursor={cursor} evByDay={evByDay} colorOf={colorForEvent} onDay={pickDay} onCreateDay={(d) => quickAt(d, 9)} onEventClick={onEventClick} canEdit={canEditEvent} onMove={moveEvent} />}
      {view === "elenco" && <ListView cursor={cursor} evByDay={evByDay} colorOf={colorForEvent} onEventClick={onEventClick} />}
      {(view === "settimana" || view === "giorno") && (
        <AgendaTimeGrid days={view === "settimana" ? weekDays : [cursor]} events={filtered}
                        colorOf={colorForEvent} nameById={nameById} onEventClick={onEventClick} onCreate={createAt}
                        onMove={moveEvent} onResize={resizeEvent} canEdit={canEditEvent} scrollKey={scrollKey} />
      )}
      {view === "richieste" && <RichiestePanel />}
      {view === "disponibilita" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1">
              {[["giorno", "Giorno"], ["settimana", "Settimana"]].map(([k, l]) => (
                <button key={k} data-testid={`avail-mode-${k}`} onClick={() => setAvailMode(k)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${availMode === k ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>{l}</button>
              ))}
            </div>
            {canReassign && <button data-testid="bulk-reassign-btn" onClick={() => setBulkOpen(true)}
                    className="px-3 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 flex items-center gap-1.5"><Users size={14} /> Riassegna in blocco</button>}
          </div>
          {availMode === "giorno"
            ? <AvailabilityGrid day={cursor} canReassign={canReassign} onSlotClick={openSlot} onBlockClick={onBlockClick} reloadKey={gridKey} />
            : <div className="space-y-4">{eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) }).map((d) => (
                <AvailabilityGrid key={d.toISOString()} day={d} title={format(d, "EEEE d MMMM", { locale: it })} canReassign={canReassign} onSlotClick={openSlot} onBlockClick={onBlockClick} reloadKey={gridKey} />
              ))}</div>}
        </div>
      )}
      </main>
      </div>

      {bulkOpen && <BulkReassignModal agents={agentsList} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); setGridKey((k) => k + 1); load(); }} />}
      {daClassOpen && <DaClassificareModal items={daClass} onClose={() => setDaClassOpen(false)} onSaved={() => { setDaClassOpen(false); setGridKey((k) => k + 1); load(); }} />}
    </div>

    <div className="md:hidden">
      <AmbitoSwitch ambito={ambito} onPick={scegliAmbito} colleghi={agentsList.filter((a) => a.id !== user?.id)} />
      <MobileAgenda reloadKey={gridKey} onNew={() => quickAt(new Date())} onCreateAt={createAt} onEventClick={onEventClick}
                    kinds={KINDS} selKinds={selKinds} toggleKind={toggleKind}
                    colorOf={colorForEvent} onBlocca={() => setBloccoOpen(true)} onEdit={(ev) => { setEditEvent(ev); setPrefill({}); setModal(true); }} />
    </div>

      {bloccoOpen && <BloccoModal onClose={() => setBloccoOpen(false)} onSaved={() => { setBloccoOpen(false); setGridKey((k) => k + 1); load(); }} />}
      {occBlock && <OccorrenzaBloccoModal e={occBlock} onClose={() => setOccBlock(null)} onSaved={() => { setOccBlock(null); setGridKey((k) => k + 1); load(); }} />}
      {quick && <QuickCreate key={quick.startISO} q={quick} onClose={() => setQuick(null)} onSaved={() => { setQuick(null); reload(); }} onMore={more} />}
      {pop && <EventPopover pop={pop} color={colorForEvent(pop.e)} nome={nameById[pop.e.owner_agent_id]} editable={canEditEvent(pop.e)}
                            onClose={() => setPop(null)} onEdit={() => { const e = pop.e; setPop(null); setEditEvent(e); setPrefill({}); setModal(true); }}
                            onDeleted={() => { setPop(null); reload(); }} />}

      {cestinoOpen && <CestinoAgenda onClose={() => setCestinoOpen(false)} onRestored={reload} />}
      {modal && <AppuntamentoModal event={editEvent} prefill={prefill}
                                   onClose={() => setModal(false)}
                                   onSaved={() => { setModal(false); setGridKey((k) => k + 1); load(); }}
                                   onDeleted={() => { setModal(false); setGridKey((k) => k + 1); load(); }} />}
    </>
  );
}

function AmbitoSwitch({ ambito, onPick, colleghi }) {
  if (!ambito) return null;
  const btn = (on) => `px-4 py-2 rounded-full text-sm font-bold transition-colors ${on ? "bg-[#2B2B2B] text-white shadow" : "text-slate-600 hover:text-[#2B2B2B]"}`;
  const singolo = ambito !== "mia" && ambito !== "tutti";
  return (
    <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="agenda-ambito">
      <div className="flex bg-slate-100 rounded-full p-1">
        <button data-testid="ambito-mia" onClick={() => onPick("mia")} className={btn(ambito === "mia")}>La mia agenda</button>
        <button data-testid="ambito-tutti" onClick={() => onPick("tutti")} className={btn(ambito === "tutti")}>Agenda di tutti</button>
      </div>
      <select data-testid="ambito-collega" value={singolo ? ambito : ""} onChange={(e) => e.target.value && onPick(e.target.value)}
              className={`text-sm rounded-full border px-3 py-2 ${singolo ? "border-[#2B2B2B] font-bold" : "border-[var(--border)]"}`}>
        <option value="">Vedi agenda di…</option>
        {colleghi.map((c) => <option key={c.id} value={c.id}>{c.name}{c.team_name ? ` · ${c.team_name}` : ""}</option>)}
      </select>
    </div>
  );
}

function CestinoAgenda({ onClose, onRestored }) {
  const [items, setItems] = useState(null);
  const S = useSelezione();
  const load = () => api.get("/agenda-cestino").then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const ripristina = async (c) => {
    try { await api.post(`/agenda-cestino/${c.id}/ripristina`); toast.success("Ripristinato"); load(); onRestored(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="agenda-cestino-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg card-immo p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1"><h3 className="font-display text-lg font-bold">Cestino agenda</h3>
          <button data-testid="agenda-cestino-chiudi" onClick={onClose} className="text-sm font-semibold text-slate-500 hover:text-[#2B2B2B]">Chiudi</button></div>
        <p className="text-xs text-[var(--muted)] mb-2">Appuntamenti eliminati negli ultimi 30 giorni: puoi recuperarli.</p>
        {items?.length > 0 && <div className="mb-3"><SelezionaTutto ids={items.map((c) => c.id)} sel={S.sel} onTutti={S.tutti} testid="agenda-cestino-seltutto" /></div>}
        <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={() => { load(); onRestored(); }}
          azioni={azioniCestino((id) => api.post(`/agenda-cestino/${id}/ripristina`), (id) => api.post(`/agenda-cestino/${id}/elimina-definitivo`, { conferma: "ELIMINA" }))} />
        {!items ? <div className="skeleton h-24 rounded-xl" /> : items.length === 0 ? <div className="text-sm text-[var(--muted)] text-center py-8">Il cestino è vuoto.</div> : (
          <div className="space-y-2">{items.map((c) => (
            <div key={c.id} data-testid={`cestino-ag-${c.id}`} className={`flex items-center gap-3 p-3 rounded-xl border ${S.sel.has(c.id) ? "border-[#FC82ED] bg-[#FC82ED]/10" : "border-[var(--border)]"}`}>
              <Casella inline on={S.sel.has(c.id)} onToggle={() => S.toggle(c.id)} testid={`cestino-ag-sel-${c.id}`} />
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{c.titolo || "Appuntamento"}{c.n > 1 ? ` (${c.n} date)` : ""}</div>
                <div className="text-[11px] text-[var(--muted)]">{c.data ? format(new Date(c.data), "d MMM yyyy HH:mm", { locale: it }) : ""} · eliminato da {c.deleted_by || "—"}</div></div>
              <button data-testid={`cestino-ag-ripristina-${c.id}`} onClick={() => ripristina(c)} className="shrink-0 px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-semibold hover:bg-slate-50">Ripristina</button>
            </div>))}</div>
        )}
      </div>
    </div>
  );
}

function BloccoModal({ onClose, onSaved }) {
  const [mode, setMode] = useState("once");
  const [titolo, setTitolo] = useState("");
  const [data, setData] = useState("");
  const [durata, setDurata] = useState(60);
  const [weekdays, setWeekdays] = useState([]);
  const [startT, setStartT] = useState("09:00");
  const [endT, setEndT] = useState("11:00");
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const DOW = [["0", "Lun"], ["1", "Mar"], ["2", "Mer"], ["3", "Gio"], ["4", "Ven"], ["5", "Sab"], ["6", "Dom"]];
  const toggleWd = (d) => setWeekdays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);
  const save = async () => {
    if (mode === "once" && !data) return toast.error("Imposta data e ora");
    if (mode === "weekly" && weekdays.length === 0) return toast.error("Seleziona almeno un giorno");
    setSaving(true);
    try {
      const payload = mode === "weekly"
        ? { kind: "weekly", titolo, weekdays: weekdays.map(Number), start_time: startT, end_time: endT, until: until || null }
        : { kind: "once", titolo, data: new Date(data).toISOString(), durata_min: parseInt(durata) || 60 };
      await api.post("/agenda/blocchi", payload);
      toast.success("Agenda bloccata creata"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Blocca agenda" onClose={onClose} onSave={save} saving={saving} saveLabel="Crea blocco" testid="blocco-modal">
      <p className="text-xs text-[var(--muted)]">Impegno personale o tra soci. Gli altri vedranno solo "Occupato". In questo orario nessuno potrà fissarti appuntamenti o inviarti richieste.</p>
      <Field label="Titolo (visibile solo a te)" testid="blocco-titolo" value={titolo} onChange={(e) => setTitolo(e.target.value)} />
      <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 w-max">
        {[["once", "Una tantum"], ["weekly", "Settimanale"]].map(([k, l]) => (
          <button key={k} data-testid={`blocco-mode-${k}`} onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mode === k ? "bg-[#111827] text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {mode === "once" ? (
        <Row>
          <Field label="Data e ora" type="datetime-local" testid="blocco-data" value={data} onChange={(e) => setData(e.target.value)} />
          <Field label="Durata (min)" type="number" testid="blocco-durata" value={durata} onChange={(e) => setDurata(e.target.value)} />
        </Row>
      ) : (
        <>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Giorni della settimana</label>
            <div className="flex gap-1.5 flex-wrap">
              {DOW.map(([d, l]) => (
                <button key={d} data-testid={`blocco-wd-${d}`} onClick={() => toggleWd(d)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${weekdays.includes(d) ? "bg-[#111827] text-white border-[#111827]" : "border-[var(--border)] text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Row>
            <Field label="Dalle" type="time" testid="blocco-start" value={startT} onChange={(e) => setStartT(e.target.value)} />
            <Field label="Alle" type="time" testid="blocco-end" value={endT} onChange={(e) => setEndT(e.target.value)} />
          </Row>
          <Field label="Fino al (opzionale)" type="date" testid="blocco-until" value={until} onChange={(e) => setUntil(e.target.value)} />
        </>
      )}
    </Modal>
  );
}

function OccorrenzaBloccoModal({ e, onClose, onSaved }) {
  const d = new Date(e.data);
  const p = (n) => String(n).padStart(2, "0");
  const occDate = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const end = new Date(d.getTime() + (e.durata_min || 60) * 60000);
  const [mode, setMode] = useState("cancel");
  const [scope, setScope] = useState(e.recurring ? "this" : "all");
  const [titolo, setTitolo] = useState(e.titolo || "");
  const [st, setSt] = useState(`${p(d.getHours())}:${p(d.getMinutes())}`);
  const [et, setEt] = useState(`${p(end.getHours())}:${p(end.getMinutes())}`);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = { action: mode, scope: e.recurring ? scope : "all", occ_date: occDate };
      if (mode === "update") { body.titolo = titolo; body.start_time = st; body.end_time = et; }
      await api.post(`/agenda/blocchi/${e.block_id}/occorrenza`, body);
      toast.success(mode === "cancel" ? "Blocco eliminato" : "Blocco aggiornato"); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Agenda bloccata" onClose={onClose} onSave={submit} saving={saving} saveLabel={mode === "cancel" ? "Conferma" : "Salva modifiche"} testid="occorrenza-modal">
      <p className="text-sm font-semibold">{e.titolo}</p>
      <p className="text-xs text-[var(--muted)] -mt-1">{format(d, "EEE d MMM HH:mm", { locale: it })}{e.recurring ? " · ricorrente" : " · una tantum"}</p>
      <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 w-max">
        {[["cancel", "Elimina"], ["update", "Modifica"]].map(([k, l]) => (
          <button key={k} data-testid={`occ-mode-${k}`} onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mode === k ? "bg-[#111827] text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {mode === "update" && (
        <>
          <Field label="Titolo" testid="occ-titolo" value={titolo} onChange={(ev) => setTitolo(ev.target.value)} />
          <Row>
            <Field label="Dalle" type="time" testid="occ-start" value={st} onChange={(ev) => setSt(ev.target.value)} />
            <Field label="Alle" type="time" testid="occ-end" value={et} onChange={(ev) => setEt(ev.target.value)} />
          </Row>
        </>
      )}
      {e.recurring && (
        <div>
          <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Ambito</label>
          <div className="flex flex-col gap-1.5">
            {[["this", "Solo questa"], ["following", "Questa e le successive"], ["all", "Tutta la serie"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" data-testid={`occ-scope-${k}`} name="occ-scope" checked={scope === k} onChange={() => setScope(k)} />{l}
              </label>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}


function DaClassificareModal({ items, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState({});
  const OPTS = TIPO_ORDER.filter((k) => k !== "bloccato");
  const assign = async (id, tipo) => {
    setSaving(true);
    try { await api.put(`/appuntamenti/${id}`, { tipo, da_classificare: false }); setDone((s) => ({ ...s, [id]: true })); toast.success("Tipo assegnato"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const rows = (items || []).filter((r) => !done[r.id]);
  return (
    <Modal title="Appuntamenti da classificare" onClose={onClose} onSave={onSaved} saving={false} saveLabel="Chiudi" testid="da-classificare-modal">
      <p className="text-xs text-[var(--muted)]">Assegna il tipo corretto; spariranno dall'elenco una volta classificati.</p>
      <div className="space-y-2 max-h-[55vh] overflow-auto">
        {rows.map((r) => (
          <div key={r.id} data-testid={`dc-${r.id}`} className="card-immo p-3">
            <div className="font-semibold text-sm truncate">{r.titolo || "Appuntamento"}</div>
            <div className="text-xs text-[var(--muted)] mb-2">{r.data ? format(new Date(r.data), "EEE d MMM HH:mm", { locale: it }) : "—"}{r.tipo ? ` · (${r.tipo})` : ""}</div>
            <div className="flex gap-1.5 flex-wrap">
              {OPTS.map((k) => (
                <button key={k} data-testid={`dc-${r.id}-${k}`} disabled={saving} onClick={() => assign(r.id, k)}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[var(--border)] hover:bg-slate-50 disabled:opacity-50">
                  <span className="w-2 h-2 rounded-full" style={{ background: TIPO_META[k].color }} />{TIPO_META[k].label}
                </button>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-6">Tutto classificato ✓</p>}
      </div>
    </Modal>
  );
}

const JS_DOW = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const toMin = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return h * 60 + m; };

function BulkReassignModal({ agents, onClose, onDone }) {
  const [agentId, setAgentId] = useState("");
  const [from, setFrom] = useState(format(new Date(), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [appts, setAppts] = useState([]);
  const [sel, setSel] = useState([]);
  const [target, setTarget] = useState("");
  const [conflicts, setConflicts] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!agentId) { setAppts([]); setSel([]); return; }
    const startISO = new Date(from + "T00:00:00").toISOString();
    const endISO = new Date(to + "T23:59:59").toISOString();
    try { const { data } = await api.get("/appuntamenti/agent", { params: { agent_id: agentId, start: startISO, end: endISO } }); setAppts(data); setSel(data.map((a) => a.id)); }
    catch { setAppts([]); setSel([]); }
    setConflicts(null); setConfirm(false);
  };
  useEffect(() => { load(); }, [agentId, from, to]); // eslint-disable-line

  const toggle = (id) => { setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]); setConfirm(false); setConflicts(null); };

  const go = async () => {
    if (!agentId || !target) return toast.error("Seleziona l'agente assente e il collega");
    if (sel.length === 0) return toast.error("Seleziona almeno un appuntamento");
    if (!confirm) {
      const res = [];
      for (const a of appts.filter((x) => sel.includes(x.id))) {
        const end = new Date(new Date(a.data).getTime() + (a.durata_min || 60) * 60000).toISOString();
        try { const { data } = await api.get("/appuntamenti/conflicts", { params: { agent_id: target, start: a.data, end, exclude: a.id } }); if (data.has_conflict) res.push({ appt: a, conflicts: data.conflicts }); } catch { /* */ }
      }
      setConflicts(res); setConfirm(true); return;
    }
    setSaving(true);
    try { const { data } = await api.post("/appuntamenti/bulk-reassign", { ids: sel, owner_agent_id: target }); toast.success(`Riassegnati ${data.reassigned.length} appuntamenti${data.errors.length ? `, ${data.errors.length} non riusciti` : ""}`); onDone(); }
    catch (e) { toast.error("Riassegnazione non riuscita"); } finally { setSaving(false); }
  };

  const colleagues = agents.filter((a) => a.id !== agentId);
  return (
    <Modal title="Riassegna in blocco" onClose={onClose} onSave={go} saving={saving}
           saveLabel={confirm ? "Conferma riassegnazione" : "Riassegna a…"} testid="bulk-reassign-modal">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-[var(--muted)]">Agente assente
          <select data-testid="bulk-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="mt-1 w-full px-2 py-2 rounded-lg border border-[var(--border)] text-sm bg-white">
            <option value="">—</option>{agents.map((a) => <option key={a.id} value={a.id}>{conRuolo(a)}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-[var(--muted)]">Dal
          <input type="date" data-testid="bulk-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full px-2 py-2 rounded-lg border border-[var(--border)] text-sm" />
        </label>
        <label className="text-xs font-semibold text-[var(--muted)]">Al
          <input type="date" data-testid="bulk-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full px-2 py-2 rounded-lg border border-[var(--border)] text-sm" />
        </label>
      </div>
      <label className="block text-xs font-semibold text-[var(--muted)] mt-1">Riassegna a
        <select data-testid="bulk-target" value={target} onChange={(e) => { setTarget(e.target.value); setConfirm(false); }} className="mt-1 w-full px-2 py-2 rounded-lg border border-[var(--border)] text-sm bg-white">
          <option value="">— scegli collega —</option>{colleagues.map((a) => <option key={a.id} value={a.id}>{conRuolo(a)}</option>)}
        </select>
      </label>
      <div className="max-h-56 overflow-y-auto space-y-1.5 mt-1" data-testid="bulk-appts">
        {appts.length === 0 ? <div className="text-sm text-[var(--muted)] py-3 text-center">Nessun appuntamento nell'intervallo.</div> : appts.map((a) => (
          <label key={a.id} data-testid={`bulk-appt-${a.id}`} className="flex items-center gap-2 p-2 rounded-lg border border-[var(--border)] cursor-pointer">
            <input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} />
            <span className="flex-1 min-w-0 text-sm"><b>{a.titolo}</b> <span className="text-xs text-[var(--muted)]">· {fmtTime(a.data)} {format(new Date(a.data), "d MMM", { locale: it })}{a.immobile_titolo ? ` · ${a.immobile_titolo}` : ""}</span></span>
          </label>
        ))}
      </div>
      {confirm && conflicts && (
        <div className={`rounded-lg border p-3 ${conflicts.length ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="bulk-conflicts">
          {conflicts.length === 0 ? <div className="text-sm text-emerald-700">Nessun conflitto per il collega selezionato. Premi di nuovo per confermare.</div> : (
            <>
              <div className="text-sm font-semibold text-amber-700 mb-1">Attenzione: {conflicts.length} conflitto/i sul collega</div>
              <ul className="text-xs text-amber-700 space-y-0.5">{conflicts.map((c, i) => <li key={i}>• {c.appt.titolo} ({fmtTime(c.appt.data)}) si sovrappone a {c.conflicts.map((x) => x.label).join(", ")}</li>)}</ul>
              <div className="text-[11px] text-amber-600 mt-1">Puoi comunque confermare.</div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function AvailabilityGrid({ day, canReassign, onSlotClick, onBlockClick, reloadKey, title }) {
  const [rows, setRows] = useState(null);
  const [drag, setDrag] = useState(null);
  const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
  const dowKey = JS_DOW[day.getDay()];

  const load = () => {
    setRows(null);
    api.get("/agenda/availability", { params: { start: dayStart.toISOString(), end: dayEnd.toISOString() } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [dayStart.getTime(), reloadKey]); // eslint-disable-line

  const Wrap = ({ children }) => (
    <div className="card-immo overflow-x-auto p-0" data-testid={`availability-grid-${format(day, "yyyy-MM-dd")}`}>
      {title && <div className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#FC82ED] border-b border-[var(--border)] bg-[#F7F7F8] capitalize">{title}</div>}
      {children}
    </div>
  );

  if (rows === null) return <Wrap><div className="p-6 flex items-center justify-center gap-2 text-sm text-[var(--muted)]"><Loader2 size={16} className="animate-spin" /> Caricamento…</div></Wrap>;
  if (rows.length === 0) return <Wrap><div className="p-10 text-center text-sm text-[var(--muted)]"><Users className="mx-auto mb-3 text-[var(--muted)]" size={24} />Nessun agente da mostrare.</div></Wrap>;

  let lo = 24 * 60, hi = 0, anyWork = false;
  rows.forEach((a) => ((a.orario || {})[dowKey] || []).forEach((iv) => { lo = Math.min(lo, toMin(iv[0])); hi = Math.max(hi, toMin(iv[1])); anyWork = true; }));
  if (!anyWork) return <Wrap><div className="p-10 text-center text-sm text-[var(--muted)]"><CalendarDays className="mx-auto mb-3 text-[var(--muted)]" size={24} />Giorno di chiusura.</div></Wrap>;

  const nSlots = Math.max(0, Math.round((hi - lo) / 30));
  const slots = Array.from({ length: nSlots }, (_, i) => { const d = new Date(day); d.setHours(0, 0, 0, 0); d.setMinutes(lo + i * 30); return d; });
  const slotIndexOf = (date) => { const mins = date.getHours() * 60 + date.getMinutes(); const idx = Math.round((mins - lo) / 30); return (idx >= 0 && idx < nSlots) ? idx : -1; };
  const inWork = (slotMin, iv) => (iv || []).some(([s, e]) => toMin(s) <= slotMin && slotMin < toMin(e));

  const agentCells = rows.map((agent) => {
    const iv = (agent.orario || {})[dowKey] || [];
    const cells = slots.map((t) => ({ type: inWork(t.getHours() * 60 + t.getMinutes(), iv) ? "free" : "off" }));
    (agent.blocks || []).forEach((b) => {
      const bs = new Date(b.start); const be = new Date(b.end);
      const si = slotIndexOf(bs); if (si < 0) return;
      const span = Math.max(1, Math.min(nSlots - si, Math.round((be - bs) / (30 * 60000))));
      cells[si] = { type: "block", block: b, span };
      for (let j = si + 1; j < si + span; j++) cells[j] = { type: "covered" };
    });
    return { agent, cells };
  });

  const blockColor = (b) => b.source === "blocco" ? "#111827" : (b.tipo && TIPO_META[b.tipo] ? TIPO_META[b.tipo].color : "#3B82F6");

  const onDrop = async (agentId, slot) => {
    if (!drag) return;
    const iso = slot.toISOString();
    const endIso = new Date(slot.getTime() + (drag.dur || 60) * 60000).toISOString();
    const agentName = rows.find((r) => r.agent_id === agentId)?.name || "l'agente";
    let warn = "";
    try {
      const { data } = await api.get("/appuntamenti/conflicts", { params: { agent_id: agentId, start: iso, end: endIso, exclude: drag.id } });
      if (data.has_conflict) warn = `\n\n⚠️ ${agentName} risulta già occupato in quell'orario.`;
    } catch { /* non-blocking */ }
    const ok = window.confirm(`Spostare "${drag.label}" a ${agentName} il ${format(slot, "EEE d MMM, HH:mm", { locale: it })}?${warn}`);
    setDrag(null);
    if (!ok) return;
    try { await api.put(`/appuntamenti/${drag.id}/reassign`, { owner_agent_id: agentId, data: iso }); toast.success("Appuntamento riassegnato"); load(); }
    catch (e) { toast.error("Riassegnazione non riuscita"); }
  };

  return (
    <Wrap>
      <table className="w-full border-collapse min-w-[720px]">
        <thead>
          <tr className="bg-[#F7F7F8]">
            <th className="w-16 p-2 text-[10px] font-bold uppercase text-[var(--muted)] border-b border-[var(--border)]">Ora</th>
            {rows.map((a) => (
              <th key={a.agent_id} className="p-2 text-xs font-semibold border-b border-l border-[var(--border)]" data-testid={`grid-agent-${a.agent_id}`}>{a.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slots.map((t, i) => (
            <tr key={i}>
              <td className="w-16 p-1 text-[10px] mono text-[var(--muted)] border-b border-[var(--border)] align-top text-right pr-2">{format(t, "HH:mm")}</td>
              {agentCells.map(({ agent, cells }) => {
                const c = cells[i];
                if (c.type === "covered") return null;
                if (c.type === "block") {
                  const b = c.block; const color = blockColor(b);
                  const draggable = canReassign && b.source === "crm";
                  const dur = Math.round((new Date(b.end) - new Date(b.start)) / 60000);
                  return (
                    <td key={agent.agent_id} rowSpan={c.span} className="p-0.5 border-b border-l border-[var(--border)] align-top">
                      <div draggable={draggable} data-testid={`grid-block-${b.appuntamento_id || b.id}`}
                           onClick={() => onBlockClick && onBlockClick(b, agent.name)}
                           onDragStart={() => draggable && setDrag({ id: b.appuntamento_id, dur, label: b.label })}
                           className={`h-full rounded-md px-1.5 py-1 text-[10px] leading-tight cursor-pointer ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
                           style={{ background: `${color}26`, borderLeft: `3px solid ${color}`, minHeight: 26 * c.span }}
                           title={`${fmtTime(b.start)}–${fmtTime(b.end)} · ${b.label}`}>
                        <div className="font-semibold truncate" style={{ color }}>{b.source === "google" ? "Occupato" : b.label}</div>
                        <div className="text-[9px] text-slate-500">{fmtTime(b.start)}</div>
                      </div>
                    </td>
                  );
                }
                if (c.type === "off") return <td key={agent.agent_id} className="border-b border-l border-[var(--border)] bg-slate-50/60" />;
                return (
                  <td key={agent.agent_id} data-testid={`grid-free-${agent.agent_id}-${format(t, "HH:mm")}`}
                      onClick={() => onSlotClick(agent.agent_id, format(t, "yyyy-MM-dd'T'HH:mm"))}
                      onDragOver={(e) => { if (drag) e.preventDefault(); }}
                      onDrop={() => onDrop(agent.agent_id, t)}
                      className="border-b border-l border-[var(--border)] bg-emerald-50 hover:bg-emerald-100 cursor-pointer transition-colors" style={{ height: 26 }}>
                    <span className="sr-only">libero</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 p-3 text-[11px] text-[var(--muted)] border-t border-[var(--border)]">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-200" /> Slot libero (clic per creare)</span>
        {canReassign && <span>Trascina un appuntamento su un altro agente o orario per riassegnarlo.</span>}
      </div>
    </Wrap>
  );
}
